import React, { useState } from 'react';
import { Provider } from 'react-redux';
import Card from '../Card';
import { listingPerPageLimit } from '../../utils/constants';
import Pagination from '../Pagination';
import LoadingSpinner from '../LoadingSpinner';
import ListingFilters from '../ListingFilters';
import ErrorBoundary from '../ErrorBoundary';
import useHolidayFinder from './hooks/useHolidayFinder';
import Intro from './elements/Intro';
import Accordion from './elements/Accordion';
import Checkbox from './elements/Checkbox';

const HolidayFinder = () => {
  const {
    waiting,
    intro,
    holidays,
    featuredHolidays,
    collections,
    destinations,
    holidayStylesFilters,
    activeFilters,
    sort,
    setActiveFilters,
    setSort,
    travelTypes,
    currentPage,
    setCurrentPage,
    updateActiveFilters,
    isActiveFilter,
    flattenActiveFilters,
  } = useHolidayFinder();

  const [showFilters, setShowFilters] = useState(false);

  const renderCards = (items) => {
    if (items.length > 0) {
      return items.map((item) => (
        <Card payload={item} type="holiday" key={`card--${item.id}`} />
      ));
    } else {
      return <p className="o-text">Sorry, there are no results to display.</p>;
    }
  };

  const numberOfPages = Math.ceil(holidays.length / listingPerPageLimit);

  let outputArray;
  if (holidays.length > listingPerPageLimit) {
    let startIndex = listingPerPageLimit * currentPage;
    startIndex = startIndex === Infinity ? 0 : startIndex;
    const endIndex = (currentPage + 1) * listingPerPageLimit;

    outputArray = holidays.slice(startIndex, endIndex);
  } else {
    outputArray = holidays;
  }

  return (
    <Provider store={window.store}>
      <div
        className={`c-holiday-finder${
          !waiting ? ' c-holiday-finder--loaded' : ''
        } o-wrapper`}
      >
        <ErrorBoundary>
          {intro && <Intro intro={intro} />}

          {waiting ? (
            <LoadingSpinner />
          ) : (
            <>
              <div className="c-holiday-finder__left o-wrapper">
                <div className="c-holiday-finder__results-block">
                  <h3 className="u-color--purple u-mb-3">
                    {featuredHolidays.length + holidays.length} Results
                  </h3>

                  <p className="o-text--small u-weight--semibold u-mb-0">
                    Refine your search:
                  </p>
                </div>

                <div
                  className={`c-holiday-finder__active-filters-list ${
                    flattenActiveFilters(activeFilters).length > 0
                      ? ''
                      : 'c-holiday-finder__active-filters-list--empty'
                  }`}
                >
                  {flattenActiveFilters(activeFilters).map((filter) => (
                    <div
                      key={`active-filter--${filter.id}`}
                      className="o-btn o-btn--active-filter"
                      onClick={() => updateActiveFilters(filter.type, filter)}
                      dangerouslySetInnerHTML={{ __html: filter.display }}
                    ></div>
                  ))}
                </div>

                <div
                  className={`c-holiday-finder__filters ${
                    showFilters ? 'c-holiday-finder__filters--active' : ''
                  }`}
                >
                  <Accordion title="Collections">
                    {collections.map((collection) => (
                      <Checkbox
                        key={`collection--${collection.id}`}
                        value={collection}
                        checked={isActiveFilter('collection', collection)}
                        onChange={(value) =>
                          updateActiveFilters('collection', value)
                        }
                      />
                    ))}
                  </Accordion>

                  <Accordion title="Destinations">
                    {destinations.map((destination) => (
                      <Checkbox
                        key={`destination--${destination.id}`}
                        value={destination}
                        checked={isActiveFilter('destination', destination)}
                        onChange={(value) =>
                          updateActiveFilters('destination', value)
                        }
                      />
                    ))}
                  </Accordion>

                  <Accordion title="Holiday Style">
                    {holidayStylesFilters.map((holidayStyle) => (
                      <Checkbox
                        key={`holiday-style--${holidayStyle.id}`}
                        value={holidayStyle}
                        checked={isActiveFilter('holidayStyle', holidayStyle)}
                        onChange={(value) =>
                          updateActiveFilters('holidayStyle', value)
                        }
                      />
                    ))}
                  </Accordion>
                </div>
              </div>

              <div className="c-holiday-finder__main">
                {featuredHolidays.length > 0 && (
                  <div className="c-holiday-finder__featured">
                    <h3>Featured Holidays</h3>

                    <div className="c-holiday-finder__cards">
                      {renderCards(featuredHolidays)}
                    </div>
                  </div>
                )}

                <div className="c-holiday-finder__non-featured">
                  <div className="c-holiday-finder__sort">
                    <ListingFilters
                      travelTypes={travelTypes}
                      setter={(value) =>
                        setActiveFilters({
                          ...activeFilters,
                          travelType: value,
                        })
                      }
                      activeFilters={activeFilters}
                      holidaysListing
                    />
                    <div className="c-holiday-finder__sort__field">
                      <label htmlFor="sort">Sort by:</label>
                      <select
                        name="sort"
                        id="sort"
                        onChange={({ target: { value } }) => setSort(value)}
                      >
                        <option value="popular">Most Popular</option>
                        <option value="new">New</option>
                        <option value="price_low">Price (low to high)</option>
                        <option value="price_high">Price (high to low)</option>
                        <option value="a-z">A - Z</option>
                        <option value="z-a">Z - A</option>
                      </select>
                    </div>
                  </div>

                  <div className="c-holiday-finder__cards c-holiday-finder__cards--main">
                    {renderCards(outputArray)}
                  </div>

                  <Pagination
                    items={holidays}
                    numberOfPages={numberOfPages}
                    setter={setCurrentPage}
                    currentPage={currentPage}
                  />
                </div>
              </div>
            </>
          )}
        </ErrorBoundary>
      </div>
    </Provider>
  );
};

export default HolidayFinder;
